import { useEffect, useRef } from "react";
import { useSettings } from "../contexts/SettingsContext";

/**
 * useAutoDismiss — one shared knob for every Dashboard click-popup.
 *
 * When `active` flips to `true`, sets a timeout that calls `onDismiss`
 * after `hover_visible_seconds` (platform setting, default 6). Any change
 * to `active` (re-open, close-then-reopen) resets the timer. When `active`
 * flips to `false` — or the component unmounts — the timer is cleared.
 *
 * Introduced 2026-02-19 (Dave) so all four Dashboard click-revealed
 * popups (Waterfall yearly detail, Reserve Activity SA-per-Member reveal,
 * Reserve Activity full-screen chart modals, Results-at-a-Glance
 * Scenarios history) share the same auto-dismiss cadence.
 */
export function useAutoDismiss(active, onDismiss) {
  const { settings } = useSettings();
  const timerRef = useRef(null);

  useEffect(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    if (!active || typeof onDismiss !== "function") return undefined;

    const secs = Math.max(0, Number(settings?.hover_visible_seconds ?? 6));
    if (secs <= 0) return undefined;

    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      onDismiss();
    }, secs * 1000);

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [active, onDismiss, settings?.hover_visible_seconds]);
}
